import { PageCard } from '../../../Compounds/CardWithTitle';
import { useEffect, useState } from 'react';
import { GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { DefaultAccountGridColumnDefs } from '../ColumnDefs/AccountGridColumnDefs';
import { GridPreferences, getPreferencesByKey, getUserPreferencesSubject } from '../../../../store/User';
import { UpstackDataGrid } from '../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../../Compounds/UpstackDataGrid/helpers';
import {
  AccountData,
  getAccountDataSubject,
  buildAccountData,
  getAccountLoadingSubject,
  useAccounts
} from '../../../../store/Accounts';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../store/GeneralStore';
import { getHiddenAssetsSubject } from '../../../../store/Inventory';
import { combineLatest } from 'rxjs';
import { getPortalUsersLoadingSubject } from '../../../../store/PortalUser';
import { getAccountCustomizationsLoadingSubject } from '../../../../store/AccountCustomization';
import { DetailsDrawer } from '../../../Compounds/DetailsDrawer';
import { useSearchParams } from 'react-router-dom';
import { getMetaData } from '../../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { HiddenRecord, InventoryAsset } from '../../../../types';

export const AccountsTab = () => {
  const [accountColumns, setAccountColumns] = useState<GridSingleSelectColDef[]>();
  const [accounts, setAccounts] = useState<PaginatedResponse<AccountData>>();
  const [gridSettings, setGridSettings] = useState<GridPreferences | null>(null);
  const [accountLoading, setAccountLoading] = useState<boolean>(false);
  const [search] = useSearchParams();
  const detailId = search.get('detailId');
  const { fetchAdminAccounts } = useAccounts();
  const [hiddenAssets, setHiddenAssets] = useState<HiddenRecord[]>([]);
  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState<boolean>(false);

  useEffect(() => {
    // Every, not some: this swaps the grid for a spinner, so one store alone would tear it down per search.
    const loadingSubscription = combineLatest([
      getAccountLoadingSubject(),
      getPortalUsersLoadingSubject(),
      getAccountCustomizationsLoadingSubject()
    ]).subscribe((data) => setAccountLoading(data.every((i) => i)));

    const settingsSub = getUserPreferencesSubject().subscribe(() => {
      const gridPrefs = getPreferencesByKey('account_grid') as GridPreferences;
      if (gridPrefs) {
        setGridSettings(gridPrefs);
        setIsPreferencesLoaded(true);
      }
    });
    const hiddenAssetsSub = getHiddenAssetsSubject().subscribe((data) => {
      setHiddenAssets(data);
    });

    const accountsSub = getAccountDataSubject().subscribe((d) => setAccounts(d));

    return () => {
      if (settingsSub) settingsSub.unsubscribe();
      if (loadingSubscription) loadingSubscription.unsubscribe();
      if (accountsSub) accountsSub.unsubscribe();
      if (hiddenAssetsSub) hiddenAssetsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (accounts?.data && accounts.data.length > 0) {
      setAccountColumns(getGridColumns(accounts.data, DefaultAccountGridColumnDefs, 'account_grid'));
    }
  }, [accounts?.data, accounts?.data?.length]);

  const handleFetchAccounts = async (filters?: ApiAdvancedFilter) => {
    await fetchAdminAccounts(filters);
    buildAccountData();
  };

  const account = accounts?.data?.find((a) => a?.id === detailId);
  // Only an inventory row has a Details control here, and an inventory row's monthly cost is never null.
  const hiddenAsset = account
    ? undefined
    : (hiddenAssets?.find((a) => a?.id === detailId) as InventoryAsset | undefined);

  return (
    <PageCard
      title="Accounts"
      dataCy="admin-accounts-tab">
      {isPreferencesLoaded && gridSettings && (
        <UpstackDataGrid
          title="Accounts"
          rows={accounts?.data || []}
          columns={accountColumns}
          loadingData={accountLoading}
          page="account_grid"
          gridSettings={gridSettings}
          showSearch={true}
          apiFilter={true}
          rowCount={accounts?.totalCount}
          filterKeys={['name']}
          fetchFunction={handleFetchAccounts}
        />
      )}
      <DetailsDrawer
        data={account || hiddenAsset}
        showDrawer={!!detailId}
        {...getMetaData(account ? (account?.isSubAgent ? 'sub-agent' : 'account') : 'inventory')}
      />
    </PageCard>
  );
};
